import { getLocaleData } from '@/selectors/localeSelectors';
import {
  getAccessFlags,
  getCenturionNetworkFlags,
  getCulinaryCollectiveFlags,
  getDigitalWaitlistFlags,
  getFeaturedLocationFlags,
  getGLCBannerFlags,
  getLocationMapFlags,
  getUpdatedPartnersAndProgramsFlags,
} from '@/selectors/configSelectors';
import { supportedMarkets } from '@/constants/supportedLocales';

export const TclMigrationFeatures = {
  CENTURION_NETWORK_PAGE: 'CENTURION_NETWORK_PAGE',
  CULINARY_COLLECTIVE_PAGE: 'CULINARY_COLLECTIVE_PAGE',
  ACCESS_PAGE: 'ACCESS_PAGE',
  DIGITAL_WAITLIST_PAGE: 'DIGITAL_WAITLIST_PAGE',
  FEATURED_LOCATION: 'FEATURED_LOCATION',
  GLC_BANNER: 'GLC_BANNER',
  LOCATION_MAP: 'LOCATION_MAP',
  UPDATED_PARTNERS_AND_PROGRAMS: 'UPDATED_PARTNERS_AND_PROGRAMS',
} as const;

export type TCLFeatures = keyof typeof TclMigrationFeatures;
type CSVString = string & { readonly __brand: 'CSVString' };
type TCLFlagSelector = (state: object) => CSVString;

export const TCL_FEATURE_FLAG_SELECTORS: Record<TCLFeatures, TCLFlagSelector> =
  {
    CULINARY_COLLECTIVE_PAGE: getCulinaryCollectiveFlags,
    CENTURION_NETWORK_PAGE: getCenturionNetworkFlags,
    ACCESS_PAGE: getAccessFlags,
    DIGITAL_WAITLIST_PAGE: getDigitalWaitlistFlags,
    FEATURED_LOCATION: getFeaturedLocationFlags,
    GLC_BANNER: getGLCBannerFlags,
    LOCATION_MAP: getLocationMapFlags,
    UPDATED_PARTNERS_AND_PROGRAMS: getUpdatedPartnersAndProgramsFlags,
  };

export const featureFlagSetCache = new Map<TCLFeatures, Set<string>>();

/* Converts a comma-separated string of flags into a list */
export const convertCSVFlagToList = <T extends string = string>(
  featureFlagsString: T
): T[] => {
  if (!featureFlagsString) {
    return [] as T[];
  }

  return featureFlagsString
    .split(',')
    .map((flag) => flag.trim())
    .filter(Boolean)
    .map((flag) => flag as T);
};

export const flagToBool = (value: unknown): boolean =>
  typeof value === 'string' && value.toLowerCase() === 'true';

export const getTCLFeatureFlagsFromState = (
  state: object
): Record<TCLFeatures, CSVString> => {
  return Object.entries(TCL_FEATURE_FLAG_SELECTORS).reduce(
    (acc, [feature, selector]) => {
      acc[feature as TCLFeatures] = selector(state);
      return acc;
    },
    {} as Record<TCLFeatures, CSVString>
  );
};

export const isTCLFeatureEnabled = ({
  feature,
  locale,
  featureFlags,
}: {
  feature: TCLFeatures;
  locale: string;
  featureFlags: Record<TCLFeatures, CSVString>;
}) => {
  const rawFlags = featureFlags[feature] || '';
  const normalizedLocale = locale.toLowerCase();
  const cachedLocales = featureFlagSetCache.get(feature);

  if (!cachedLocales) {
    featureFlagSetCache.set(
      feature,
      new Set(convertCSVFlagToList(rawFlags).map((flag) => flag.toLowerCase()))
    );
  }

  const featureLocales = featureFlagSetCache.get(feature);
  return featureLocales ? featureLocales.has(normalizedLocale) : false;
};

export const canViewTCL = ({
  state,
  feature,
}: {
  state: object;
  feature: TCLFeatures;
}) => {
  const { locale } = getLocaleData(state);
  const featureFlags = getTCLFeatureFlagsFromState(state);

  return isTCLFeatureEnabled({
    feature,
    locale,
    featureFlags,
  });
};

export const parseEnabledMarkets = (
  enabledMarketsString: string
): Set<string> => {
  if (!enabledMarketsString) {
    return new Set<string>();
  }

  return new Set(
    enabledMarketsString
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean)
  );
};

export const getFilteredSupportedMarkets = (enabledMarketsConfig: string) => {
  const enabledSet = parseEnabledMarkets(enabledMarketsConfig);

  if (enabledSet.size === 0) {
    return supportedMarkets;
  }

  return supportedMarkets
    .map((market) => ({
      ...market,
      products: market.products.filter((product) =>
        enabledSet.has(`${market.locale}:${product.name}`)
      ),
    }))
    .filter((market) => market.products.length > 0);
};
